import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/auth-context';
import { LoginPage } from '@/features/auth/login-page';
import { DashboardSelector } from '@/features/dashboard/dashboard-selector';
import { DashboardLayout } from '@/features/dashboard/dashboard-layout';
// CRM / Customer Operations
import { AccountsPage } from '@/features/crm/accounts-page';
import { AccountDetailPage } from '@/features/crm/account-detail-page';
import { AccountForm } from '@/features/crm/account-form';
import { ContactsPage } from '@/features/crm/contacts-page';
import { DealsPage } from '@/features/crm/deals-page';
import { ActivitiesPage } from '@/features/crm/activities-page';
import { DocumentsPage } from '@/features/crm/documents-page';
import { CalendarPage } from '@/features/dashboard/calendar-page';
import { OutlookSyncPage } from '@/features/dashboard/outlook-sync-page';
// Helpdesk & Support
import { TicketsPage } from '@/features/support/tickets-page';
import { TicketDetailPage } from '@/features/support/ticket-detail-page';
import { KnowledgeBasePage } from '@/features/support/knowledge-base-page';
import { CannedResponsesPage } from '@/features/support/canned-responses-page';
import { SlaPoliciesPage } from '@/features/support/sla-policies-page';
import { AutomationRulesPage } from '@/features/support/automation-rules-page';
import { AppStatusManagementPage } from '@/features/support/app-status-page';
import { MaintenanceWindowsPage } from '@/features/support/maintenance-page';
import { HelpdeskAnalyticsPage } from '@/features/support/helpdesk-analytics-page';
// Billing & Finance
import { FinanceOverview } from '@/features/finance/finance-overview';
import { BusinessProfilesPage } from '@/features/finance/business-profiles-page';
import { ApplicationsPage } from '@/features/finance/applications-page';
import { InvoiceForm } from '@/features/finance/invoice-form';
import { InvoiceDetailPage } from '@/features/finance/invoice-detail-page';
import { ContractsPage } from '@/features/finance/contracts-page';
import { RecurringSchedulesPage } from '@/features/finance/recurring-schedules-page';
import { RecurringScheduleForm } from '@/features/finance/recurring-schedule-form';
import { GenerateInvoicesPage } from '@/features/finance/generate-invoices-page';
import { AlertsPage } from '@/features/finance/alerts-page';
import { ExpensesTabsPage } from '@/features/finance/expenses-tabs-page';
import { RevenueTabsPage } from '@/features/finance/revenue-tabs-page';
import { BillPayPage } from '@/features/finance/bill-pay-page';
import { BudgetsPage } from '@/features/finance/budgets-page';
import { AssetsPage } from '@/features/finance/assets-page';
import { FinancialStatementsPage } from '@/features/finance/financial-statements-page';
import { ForecastPage } from '@/features/finance/forecast-page';
// Tax & Compliance
import { TaxOverview } from '@/features/tax/tax-overview';
import { DeductionsPage } from '@/features/tax/deductions-page';
import { TaxFormsPage } from '@/features/tax/tax-forms-page';
import { EstimatedTaxesPage } from '@/features/tax/estimated-taxes-page';
import { AuditLogPage } from '@/features/tax/audit-log-page';
import { TaxProjectionsPage } from '@/features/tax/tax-projections-page';
// Project Management
import { ProjectsOverview } from '@/features/projects/projects-overview';
import { ProjectsPage } from '@/features/projects/projects-page';
import { ProjectDetailPage } from '@/features/projects/project-detail-page';
import { ProjectForm } from '@/features/projects/project-form';
import { TasksPage } from '@/features/projects/tasks-page';
import { TimeEntriesPage } from '@/features/projects/time-entries-page';
import { RequirementsPage } from '@/features/projects/requirements-page';
import { RisksPage } from '@/features/projects/risks-page';
// Analytics & Reports
import { AnalyticsOverview } from '@/features/analytics/analytics-overview';
import { ReportBuilderPage } from '@/features/analytics/report-builder-page';
import { ScheduledReportsPage } from '@/features/analytics/scheduled-reports-page';
// System Admin
import { SystemStatusPage } from '@/features/analytics/system-status-page';
import { WidgetManagementPage } from '@/features/analytics/widget-management-page';
import { LookupAdminPage } from '@/features/analytics/lookup-admin-page';
import { ApprovalsPage } from '@/features/analytics/approvals-page';
import { NotificationPrefsPage } from '@/features/support/notification-prefs-page';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <DashboardSelector />
          </PrivateRoute>
        }
      />

      {/* 1. Customer Operations — Sales / Account Managers */}
      <Route
        path="/customer-operations"
        element={
          <PrivateRoute>
            <DashboardLayout dashboard="customer_operations" />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="accounts" replace />} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="accounts/new" element={<AccountForm />} />
        <Route path="accounts/:id" element={<AccountDetailPage />} />
        <Route path="accounts/:id/edit" element={<AccountForm />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="deals" element={<DealsPage />} />
        <Route path="activities" element={<ActivitiesPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="outlook-sync" element={<OutlookSyncPage />} />
      </Route>

      {/* 2. Helpdesk & Support — IT / Support Team */}
      <Route
        path="/helpdesk"
        element={
          <PrivateRoute>
            <DashboardLayout dashboard="helpdesk_support" />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="tickets" replace />} />
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="tickets/:id" element={<TicketDetailPage />} />
        <Route path="knowledge-base" element={<KnowledgeBasePage />} />
        <Route path="canned-responses" element={<CannedResponsesPage />} />
        <Route path="sla-policies" element={<SlaPoliciesPage />} />
        <Route path="automation-rules" element={<AutomationRulesPage />} />
        <Route path="app-status" element={<AppStatusManagementPage />} />
        <Route path="maintenance" element={<MaintenanceWindowsPage />} />
        <Route path="analytics" element={<HelpdeskAnalyticsPage />} />
      </Route>

      {/* 3. Billing & Finance — Finance Team */}
      <Route
        path="/billing"
        element={
          <PrivateRoute>
            <DashboardLayout dashboard="billing_finance" />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<FinanceOverview />} />
        {/* Revenue tab page (Payments + Invoices + Other Income) */}
        <Route path="revenue" element={<RevenueTabsPage />} />
        <Route path="invoices" element={<RevenueTabsPage />} />
        <Route path="invoices/new" element={<InvoiceForm />} />
        <Route path="invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="invoices/:id/edit" element={<InvoiceForm />} />
        <Route path="payments" element={<RevenueTabsPage />} />
        <Route path="other-income" element={<RevenueTabsPage />} />
        <Route path="contracts" element={<ContractsPage />} />
        <Route path="recurring" element={<RecurringSchedulesPage />} />
        <Route path="recurring/new" element={<RecurringScheduleForm />} />
        <Route path="recurring/:id" element={<RecurringScheduleForm />} />
        <Route path="generate" element={<GenerateInvoicesPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="business-profiles" element={<BusinessProfilesPage />} />
        <Route path="applications" element={<ApplicationsPage />} />
        {/* Expenses tab page (Expenses + Subscriptions + Charge Accounts + Loans) */}
        <Route path="expenses" element={<ExpensesTabsPage />} />
        <Route path="subscriptions" element={<ExpensesTabsPage />} />
        <Route path="charge-accounts" element={<ExpensesTabsPage />} />
        <Route path="loans" element={<ExpensesTabsPage />} />
        <Route path="bill-pay" element={<BillPayPage />} />
        <Route path="budgets" element={<BudgetsPage />} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="statements" element={<FinancialStatementsPage />} />
        <Route path="forecast" element={<ForecastPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
      </Route>

      {/* 4. Tax & Compliance — Finance / Accounting */}
      <Route
        path="/tax"
        element={
          <PrivateRoute>
            <DashboardLayout dashboard="tax_compliance" />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<TaxOverview />} />
        <Route path="deductions" element={<DeductionsPage />} />
        <Route path="forms" element={<TaxFormsPage />} />
        <Route path="estimated" element={<EstimatedTaxesPage />} />
        <Route path="projections" element={<TaxProjectionsPage />} />
        <Route path="audit-log" element={<AuditLogPage />} />
      </Route>

      {/* 5. Project Management — Delivery / Dev Team */}
      <Route
        path="/projects"
        element={
          <PrivateRoute>
            <DashboardLayout dashboard="project_management" />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<ProjectsOverview />} />
        <Route path="list" element={<ProjectsPage />} />
        <Route path="list/new" element={<ProjectForm />} />
        <Route path="list/:id" element={<ProjectDetailPage />} />
        <Route path="list/:id/edit" element={<ProjectForm />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="time-entries" element={<TimeEntriesPage />} />
        <Route path="requirements" element={<RequirementsPage />} />
        <Route path="risks" element={<RisksPage />} />
      </Route>

      {/* 6. Analytics & Reports — Leadership / All */}
      <Route
        path="/analytics"
        element={
          <PrivateRoute>
            <DashboardLayout dashboard="analytics" />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<AnalyticsOverview />} />
        <Route path="report-builder" element={<ReportBuilderPage />} />
        <Route path="scheduled-reports" element={<ScheduledReportsPage />} />
      </Route>

      {/* 7. System Admin — Admins Only */}
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <DashboardLayout dashboard="system_admin" />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="system-status" replace />} />
        <Route path="system-status" element={<SystemStatusPage />} />
        <Route path="widget-management" element={<WidgetManagementPage />} />
        <Route path="lookup-admin" element={<LookupAdminPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="notification-prefs" element={<NotificationPrefsPage />} />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
